import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Brain,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Monitor,
} from 'lucide-react';
import { SeverityBadge } from '../ui/SeverityBadge';
import { Button } from '../ui/Button';
import { InlineFeedback } from '../ui/InlineFeedback';
import { StatusManager } from './StatusManager';
import { AssignmentPicker } from './AssignmentPicker';
import { AnalystNotes } from './AnalystNotes';
import { QuickAcknowledgeButton } from './IncidentQueueCardList';
import { cn, formatRelativeTime, getRiskScoreColor } from '../../lib/utils';
import type { Alert, EnrichedIncident, PlatformStatus } from '../../data/types';

interface MobileTriageViewProps {
  tenantId: string;
  incidentId: string;
  incident: EnrichedIncident;
  alerts: Alert[];
  displayStatus: PlatformStatus;
  displayAssigned: { userId: string | null; name: string | null };
  onStatusChange: (status: PlatformStatus) => void;
  onAssign: (userId: string | null, name: string | null) => void;
  onRequestDesktop: () => void;
}

function MobileAlertRow({ alert }: { alert: Alert }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-border-default last:border-0">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center gap-2 px-4 py-3 min-h-11 text-left"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 shrink-0 text-text-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 shrink-0 text-text-muted" />
        )}
        <SeverityBadge severity={alert.severity} />
        <span className="flex-1 min-w-0 truncate text-sm text-text-primary">{alert.title}</span>
        <span className="shrink-0 text-xs font-mono text-text-muted">
          {formatRelativeTime(alert.createdDateTime)}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pl-10 space-y-2 text-sm text-text-secondary">
          {alert.description ? (
            <p className="whitespace-pre-wrap">{alert.description}</p>
          ) : (
            <p className="text-text-muted">No description available.</p>
          )}
          {alert.detectionSource && (
            <p className="text-xs text-text-muted">
              Detection source: <span className="font-mono">{alert.detectionSource}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Condensed incident detail for phones: header, acknowledge/status/assign
 * actions, AI summary, alerts and notes. Evidence, Timeline, Raw Data,
 * Enrichment and Investigate stay desktop-only.
 */
export function MobileTriageView({
  tenantId,
  incidentId,
  incident,
  alerts,
  displayStatus,
  displayAssigned,
  onStatusChange,
  onAssign,
  onRequestDesktop,
}: MobileTriageViewProps) {
  const ai = incident.aiEnrichment;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Back navigation */}
      <Link
        to="/incidents"
        className="inline-flex items-center gap-2 min-h-11 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to Incidents</span>
      </Link>

      {/* Header card */}
      <div className="bg-bg-secondary rounded-xl border border-border-default p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityBadge severity={incident.severity} />
          {ai?.riskScore !== undefined && ai.riskScore !== null && (
            <span className="ml-auto flex items-center gap-1.5">
              <span className="text-xs text-text-secondary">Risk</span>
              <span
                className="text-lg font-mono font-bold"
                style={{ color: getRiskScoreColor(ai.riskScore) }}
              >
                {ai.riskScore.toFixed(1)}
              </span>
              <InlineFeedback tenantId={tenantId} incidentId={incidentId} feedbackType="risk" />
            </span>
          )}
        </div>
        <h1 className="mt-2 text-lg font-semibold text-text-primary">{incident.title}</h1>
        <div className="mt-1 text-xs text-text-secondary font-mono">
          {incident.incidentId} · {incident.tenantDisplayName || incident.tenantId} ·{' '}
          {formatRelativeTime(incident.createdTime)}
        </div>
        {incident.defenderWebUrl && (
          <a
            href={incident.defenderWebUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3"
          >
            <Button variant="ghost" size="sm">
              <ExternalLink className="w-4 h-4" />
              Open in Defender
            </Button>
          </a>
        )}
      </div>

      {/* Sticky action bar */}
      <div className="sticky top-14 z-10 bg-bg-primary/95 backdrop-blur py-2 -mx-4 px-4 border-b border-border-default space-y-2">
        {displayStatus === 'new' && (
          <QuickAcknowledgeButton
            incident={incident}
            onAcknowledged={(updated) => onStatusChange(updated.platformStatus)}
            className="w-full min-h-12"
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <StatusManager
            tenantId={tenantId}
            incidentId={incidentId}
            currentStatus={displayStatus}
            onStatusChange={onStatusChange}
            closeContext={{
              severity: incident.severity,
              riskScore: incident.riskScore ?? null,
              escalate: incident.aiEscalate ?? null,
              recommendedAction: incident.aiImmediateAction ?? null,
              alertCount: alerts.length,
              truePositiveAlertCount: alerts.filter(a => (a.classification ?? '').toLowerCase() === 'truepositive').length,
            }}
          />
          <AssignmentPicker
            tenantId={tenantId}
            incidentId={incidentId}
            assignedToUserId={displayAssigned.userId}
            assignedToName={displayAssigned.name}
            onAssign={onAssign}
          />
        </div>
      </div>

      {/* AI summary */}
      {ai?.summary && (
        <div className="bg-bg-secondary rounded-xl border border-border-default p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="flex items-center gap-2 text-sm font-medium text-text-primary">
              <Brain className="w-4 h-4 text-accent-blue" />
              AI Summary
            </h2>
            <InlineFeedback tenantId={tenantId} incidentId={incidentId} feedbackType="summary" />
          </div>
          <p className="text-sm text-text-secondary whitespace-pre-wrap">{ai.summary}</p>
        </div>
      )}

      {/* Alerts */}
      <div className="bg-bg-secondary rounded-xl border border-border-default overflow-hidden">
        <div className="px-4 py-3 border-b border-border-default bg-bg-tertiary">
          <h2 className="text-sm font-medium text-text-primary">
            Alerts <span className="text-text-secondary">({alerts.length})</span>
          </h2>
        </div>
        {alerts.length > 0 ? (
          alerts.map((alert) => <MobileAlertRow key={alert.alertId} alert={alert} />)
        ) : (
          <p className="px-4 py-6 text-sm text-text-secondary text-center">
            No alerts on this incident.
          </p>
        )}
      </div>

      {/* Notes */}
      <AnalystNotes tenantId={tenantId} incidentId={incidentId} />

      {/* Desktop deferral */}
      <div className={cn('bg-bg-secondary rounded-xl border border-border-default p-4', 'space-y-3')}>
        <p className="flex items-start gap-2 text-sm text-text-secondary">
          <Monitor className="w-4 h-4 mt-0.5 shrink-0 text-text-muted" />
          Evidence, Timeline, Raw Data, Enrichment and Investigate are available in the desktop
          view.
        </p>
        <Button variant="secondary" onClick={onRequestDesktop} className="w-full">
          Use desktop layout
        </Button>
      </div>
    </div>
  );
}
